<template>
  <a-spin :spinning="loading">
    <a-card :title="$t('label.workspace.settings.defaults')">
      <a-alert type="info" show-icon style="margin-bottom: 16px" :message="$t('message.workspace.settings.defaults')" />

      <a-form layout="vertical">
        <a-form-item :label="$t('label.workspace.vdi.image')">
          <a-select
            v-model:value="templateId"
            show-search
            optionFilterProp="label"
            :options="templateOptions"
            :disabled="savingDefaults || !canUpdateDefaults"
          />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.vdi.compute.offering')">
          <a-select
            v-model:value="serviceOfferingId"
            show-search
            optionFilterProp="label"
            :options="serviceOfferingOptions"
            :disabled="savingDefaults || !canUpdateDefaults"
          />
        </a-form-item>
        <a-space>
          <a-button type="primary" :loading="savingDefaults" :disabled="!canSaveDefaults" @click="saveDefaults">
            {{ $t('label.save') }}
          </a-button>
          <a-button :disabled="savingDefaults || !defaultsChanged" @click="resetDefaults">
            {{ $t('label.reset') }}
          </a-button>
        </a-space>
      </a-form>
    </a-card>

    <template v-if="isHorizon">
      <a-card :title="$t('label.workspace.duo')" style="margin-top: 16px">
        <a-row type="flex" justify="space-between" align="middle" :gutter="16">
          <a-col :xs="24" :md="14">
            <p>{{ $t('message.workspace.duo') }}</p>
            <a-tag :color="duoStatusColor">{{ duoStatusLabel }}</a-tag>
          </a-col>
          <a-col :xs="24" :md="10" class="integration-actions">
            <a-space v-if="!duoConfigured">
              <a-button
                type="primary"
                :disabled="integrationRunning || !canConfigureDuo || showDuoForm"
                @click="showDuoForm = true"
              >
                {{ $t('label.workspace.duo.configure') }}
              </a-button>
            </a-space>
            <a-space v-else>
              <a-button
                :type="duoPaused ? 'primary' : 'default'"
                :loading="runningAction === (duoPaused ? 'unpause-duo' : 'pause-duo')"
                :disabled="integrationRunning || !canConfigureDuo"
                @click="runDuoAction(duoPaused ? 'unpause' : 'pause')"
              >
                {{ duoPaused ? $t('label.workspace.duo.unpause') : $t('label.workspace.duo.pause') }}
              </a-button>
              <a-button
                danger
                :loading="runningAction === 'disable-duo'"
                :disabled="integrationRunning || !canConfigureDuo"
                @click="runDuoAction('disable')"
              >
                {{ $t('label.workspace.duo.disable') }}
              </a-button>
            </a-space>
          </a-col>
        </a-row>

        <a-alert
          v-if="!canConfigureDuo"
          type="warning"
          show-icon
          style="margin-top: 16px"
          :message="$t('message.workspace.integration.contact')"
        />

        <div class="form-help integration-help">{{ $t('message.workspace.duo.lifecycle') }}</div>
      </a-card>

      <a-card :title="$t('label.workspace.entra.sync')" style="margin-top: 16px">
        <a-row type="flex" justify="space-between" align="middle" :gutter="16">
          <a-col :xs="24" :md="14">
            <p>{{ entraDescription }}</p>
            <a-tag :color="entraStatusColor">{{ entraStatusLabel }}</a-tag>
          </a-col>
          <a-col :xs="24" :md="10" class="integration-actions">
            <a-button
              v-if="!entraInstalled"
              type="primary"
              :loading="runningAction === 'install-entra'"
              :disabled="integrationRunning || !canInstallEntra"
              @click="confirmEntraInstall"
            >
              {{ $t('label.workspace.entra.install') }}
            </a-button>
            <a-button
              v-else-if="entraConfigured"
              :type="entraPaused ? 'primary' : 'default'"
              :loading="runningAction === (entraPaused ? 'unpause-entra' : 'pause-entra')"
              :disabled="integrationRunning || !canManageEntra"
              @click="runEntraAction(entraPaused ? 'unpause' : 'pause')"
            >
              {{ entraPaused ? $t('label.workspace.entra.unpause') : $t('label.workspace.entra.pause') }}
            </a-button>
          </a-col>
        </a-row>

        <a-alert
          v-if="!entraInstalled && !canInstallEntra"
          type="warning"
          show-icon
          style="margin-top: 16px"
          :message="$t('message.workspace.integration.contact')"
        />
      </a-card>
    </template>

    <a-modal
      :title="$t('label.workspace.duo.configure')"
      :visible="showDuoForm"
      :confirm-loading="runningAction === 'configure-duo'"
      :ok-button-props="{ disabled: integrationRunning || !duoFormValid }"
      :cancel-button-props="{ disabled: integrationRunning }"
      :ok-text="$t('label.workspace.duo.deploy')"
      :cancel-text="$t('label.cancel')"
      @ok="configureDuo"
      @cancel="cancelDuoConfiguration"
    >
      <a-form layout="vertical" @submit.prevent="configureDuo">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom: 16px"
          :message="$t('message.workspace.duo.credentials')"
        />
        <a-form-item :label="$t('label.workspace.duo.integration.key')" required>
          <a-input v-model:value="duoIntegrationKey" autocomplete="off" placeholder="DIXXXXXXXXXXXXXXXXXX" />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.duo.secret.key')" required>
          <a-input-password v-model:value="duoSecretKey" autocomplete="new-password" />
        </a-form-item>
        <a-form-item :label="$t('label.workspace.duo.api.host')" required>
          <a-input v-model:value="duoApiHost" autocomplete="off" placeholder="api-XXXXXXXX.duosecurity.com" />
          <div class="form-help">{{ $t('message.workspace.duo.api.host') }}</div>
        </a-form-item>
      </a-form>
    </a-modal>
  </a-spin>
</template>

<script>
import AnsibleMixin from './AnsibleMixin'

const asBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1'

export default {
  name: 'WorkspaceSettings',
  mixins: [AnsibleMixin],
  props: {
    workspaceId: { type: String, required: true }
  },
  data () {
    return {
      workspace: {},
      templates: [],
      serviceOfferings: [],
      templateId: null,
      serviceOfferingId: null,
      savedTemplateId: null,
      savedServiceOfferingId: null,
      loading: true,
      savingDefaults: false,
      runningAction: null,
      duoConfigured: false,
      duoPaused: false,
      showDuoForm: false,
      duoIntegrationKey: '',
      duoSecretKey: '',
      duoApiHost: '',
      entraInstalled: false,
      entraConfigured: false,
      entraPaused: false
    }
  },
  computed: {
    availableApis () {
      return this.$store.getters.apis || {}
    },
    canUpdateDefaults () {
      return 'workspaceUpdate' in this.availableApis
    },
    canConfigureDuo () {
      return 'workspaceAuthorizeConfigureDuo' in this.availableApis
    },
    canInstallEntra () {
      return 'workspaceAuthorizeInstallEntraSync' in this.availableApis
    },
    canManageEntra () {
      return 'workspaceAuthorizeManageEntraSync' in this.availableApis
    },
    isHorizon () {
      return this.workspace.brokerType === 'horizon'
    },
    integrationRunning () {
      return Boolean(this.runningAction)
    },
    defaultsChanged () {
      return this.templateId !== this.savedTemplateId || this.serviceOfferingId !== this.savedServiceOfferingId
    },
    canSaveDefaults () {
      return (
        this.canUpdateDefaults &&
        this.defaultsChanged &&
        this.templateId &&
        this.serviceOfferingId &&
        !this.savingDefaults
      )
    },
    templateOptions () {
      return this.templates.map((template) => ({ label: template.name, value: template.id }))
    },
    serviceOfferingOptions () {
      return this.serviceOfferings.map((offering) => ({
        label: `${offering.name} - ${offering.cpunumber}vCPU & ${this.humanReadableMemory(offering.memory)} RAM`,
        value: offering.id
      }))
    },
    duoFormValid () {
      return (
        this.duoIntegrationKey.trim().length > 0 &&
        this.duoSecretKey.trim().length > 0 &&
        /^api-[A-Za-z0-9.-]+\.duosecurity\.com$/.test(this.duoApiHost.trim())
      )
    },
    duoStatusColor () {
      return !this.duoConfigured ? 'default' : this.duoPaused ? 'orange' : 'green'
    },
    duoStatusLabel () {
      return !this.duoConfigured
        ? this.$t('label.workspace.integration.disabled')
        : this.duoPaused
          ? this.$t('label.workspace.integration.paused')
          : this.$t('label.workspace.integration.enabled')
    },
    entraStatusColor () {
      return !this.entraInstalled ? 'default' : !this.entraConfigured || this.entraPaused ? 'orange' : 'green'
    },
    entraStatusLabel () {
      if (!this.entraInstalled) return this.$t('label.workspace.entra.not.installed')
      if (!this.entraConfigured) return this.$t('label.workspace.entra.configuration.required')
      return this.entraPaused
        ? this.$t('label.workspace.integration.paused')
        : this.$t('label.workspace.integration.running')
    },
    entraDescription () {
      if (!this.entraInstalled) return this.$t('message.workspace.entra.not.installed')
      if (!this.entraConfigured) return this.$t('message.workspace.entra.not.configured')
      return this.$t('message.workspace.entra.installed')
    }
  },
  created () {
    this.load()
  },
  methods: {
    humanReadableMemory (megabytes) {
      if (megabytes >= 1024) return `${Math.round(megabytes / 102.4) / 10}GB`
      return `${megabytes}MB`
    },
    extraFields (appliance) {
      const fields = appliance?.extraFields || appliance?.extrafields || {}
      if (typeof fields !== 'string') return fields
      try {
        return JSON.parse(fields)
      } catch (_) {
        return {}
      }
    },
    async load () {
      this.loading = true
      try {
        const workspaceJson = await this.asyncApi('workspaceGet', { uuid: this.workspaceId })
        this.workspace = workspaceJson.workspaceresponse.payload.workspaces[0]
        this.templateId = this.workspace.vdiTemplateId
        this.serviceOfferingId = this.workspace.vdiServiceOfferingId
        this.savedTemplateId = this.templateId
        this.savedServiceOfferingId = this.serviceOfferingId

        const [templatesJson, offeringsJson, appliancesJson] = await Promise.all([
          this.asyncApi('listTemplates', {
            listall: true,
            details: 'all',
            templatefilter: 'executable',
            zoneid: this.workspace.zoneId
          }),
          this.asyncApi('listServiceOfferings', { listall: true, zoneid: this.workspace.zoneId }),
          this.asyncApi('workspaceApplianceList', { workspace_uuid: this.workspace.uuid })
        ])
        this.templates = templatesJson.listtemplatesresponse.template || []
        this.serviceOfferings = offeringsJson.listserviceofferingsresponse.serviceoffering || []

        const appliances = appliancesJson.workspaceappliancelistresponse.payload.appliances || []
        const brokerFields = this.extraFields(appliances.find((appliance) => appliance.type === 'broker'))
        const dcFields = this.extraFields(appliances.find((appliance) => appliance.type === 'ad-server'))
        const legacyDuoEnabled = asBoolean(brokerFields.duoEnabled)
        this.duoConfigured =
          brokerFields.duoConfigured === undefined ? legacyDuoEnabled : asBoolean(brokerFields.duoConfigured)
        this.duoPaused = asBoolean(brokerFields.duoPaused)
        this.entraInstalled = asBoolean(dcFields.entraSyncInstalled)
        this.entraConfigured = asBoolean(dcFields.entraSyncConfigured)
        this.entraPaused = asBoolean(dcFields.entraSyncPaused)
      } catch (error) {
        this.$notification.error({
          message: this.$t('label.error'),
          description: this.$t('message.workspace.settings.load.failed')
        })
      } finally {
        this.loading = false
      }
    },
    resetDefaults () {
      this.templateId = this.savedTemplateId
      this.serviceOfferingId = this.savedServiceOfferingId
    },
    async saveDefaults () {
      this.savingDefaults = true
      try {
        await this.asyncApi('workspaceUpdate', {
          uuid: this.workspace.uuid,
          vdi_template_id: this.templateId,
          vdi_service_offering_id: this.serviceOfferingId
        })
        this.savedTemplateId = this.templateId
        this.savedServiceOfferingId = this.serviceOfferingId
        this.workspace.vdiTemplateId = this.templateId
        this.workspace.vdiServiceOfferingId = this.serviceOfferingId
        this.$notification.success({
          message: this.$t('label.success'),
          description: this.$t('message.workspace.settings.saved')
        })
      } catch (error) {
        this.$notification.error({
          message: this.$t('label.error'),
          description: this.$t('message.workspace.settings.save.failed')
        })
      } finally {
        this.savingDefaults = false
      }
    },
    cancelDuoConfiguration () {
      this.showDuoForm = false
      this.duoIntegrationKey = ''
      this.duoSecretKey = ''
      this.duoApiHost = ''
    },
    async configureDuo () {
      if (!this.duoFormValid) return
      const succeeded = await this.runDuoAction('configure', {
        integrationKey: this.duoIntegrationKey.trim(),
        secretKey: this.duoSecretKey.trim(),
        apiHost: this.duoApiHost.trim().toLowerCase()
      })
      if (succeeded) this.cancelDuoConfiguration()
    },
    async runDuoAction (action, configuration) {
      const variables = { 'workspace.duo.action': action }
      if (configuration) {
        variables['workspace.duo.integration.key'] = configuration.integrationKey
        variables['workspace.duo.secret.key'] = configuration.secretKey
        variables['workspace.duo.api.host'] = configuration.apiHost
      }
      return this.runIntegrationAction({
        actionName: `${action}-duo`,
        playbook: 'configure-workspace-duo',
        variables
      })
    },
    confirmEntraInstall () {
      this.$confirm({
        title: this.$t('label.workspace.entra.install.confirm'),
        content: this.$t('message.workspace.entra.install.confirm'),
        okText: this.$t('label.workspace.entra.install.reboot'),
        cancelText: this.$t('label.cancel'),
        onOk: () => this.runEntraAction('install')
      })
    },
    async runEntraAction (action) {
      return this.runIntegrationAction({
        actionName: `${action}-entra`,
        playbook: 'manage-workspace-entra-sync',
        variables: { 'workspace.entra.sync.action': action }
      })
    },
    async runIntegrationAction ({ actionName, playbook, variables }) {
      this.runningAction = actionName
      try {
        const params = await this.getAnsibleBaseParams(this.workspace)
        const configurationJson = await this.asyncApi('workspaceGetConfiguration')
        this.copyObject(configurationJson.workspaceconfigurationresponse.payload.configuration, params)
        Object.assign(params, variables)
        const task = await this.asyncApi('ansibleStartTaskAsync', {
          playbook,
          vars: this.encodeVars(this.convertNamespaces(params))
        })
        await this.waitForTask(
          task.ansiblestarttaskasyncresponse.jobid,
          this.$t('message.workspace.integration.started'),
          this.$t('message.workspace.integration.failed'),
          this.$t('message.workspace.integration.completed')
        )
        await this.load()
        return true
      } catch (error) {
        this.$notification.error({
          message: this.$t('label.error'),
          description: this.$t('message.workspace.integration.failed')
        })
        return false
      } finally {
        this.runningAction = null
      }
    }
  }
}
</script>

<style scoped>
.form-help {
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
  margin-top: 4px;
}
.integration-help {
  margin-top: 16px;
}
.integration-actions {
  margin-top: 8px;
  text-align: right;
}
@media (max-width: 767px) {
  .integration-actions {
    text-align: left;
  }
}
</style>
